import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import SearchBar from './components/SearchBar';
import ExositeCard from './components/ExositeCard';
import ProteinViewer from './components/ProteinViewer';
import Browse from './components/Browse';
import APIComponent from './components/API';
import NotFoundSuggestions from './components/NotFoundSuggestions';
import ExportModal from './components/ExportModal';
import ExositePrediction from './components/ExositePrediction';
import VisitorMapFooter, { VisitorStats } from './components/VisitorMapFooter';
import { ExositeEntry, SearchFilters, DatabaseStats } from './types';

const queryClient = new QueryClient();

// Utility function to get the appropriate backend URL
const getBackendUrl = () => {
  // Handle different access patterns (local, external, proxy)
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // If accessing via cnptia.embrapa.br domain over HTTPS, use the API proxy endpoint
  if (hostname.includes('cnptia.embrapa.br') && protocol === 'https:') {
    // Use relative path to avoid mixed content issues - assumes API is proxied
    return '/api';
  }
  
  // If accessing via virt0042.cnptia.embrapa.br, use the direct backend
  if (hostname.includes('cnptia.embrapa.br') || hostname.includes('virt0042')) {
    return `http://virt0042.cnptia.embrapa.br:8000`;
  }
  
  // For local development or direct IP access
  return `http://${hostname}:8000`;
};

// Simple SVG icons as components
const BeakerIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2z" />
  </svg>
);


const CheckCircleIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

function App() {
  const [searchResults, setSearchResults] = useState<ExositeEntry[]>([]);
  const [allEntries, setAllEntries] = useState<ExositeEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<ExositeEntry | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [showFullscreenViewer, setShowFullscreenViewer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [visitorStats, setVisitorStats] = useState<VisitorStats | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'stingexodb' | 'stingexofind' | 'about' | 'suite'>('stingexodb');
  const [activeSubTab, setActiveSubTab] = useState<'search' | 'browse' | 'api'>('search');
  const [currentSearch, setCurrentSearch] = useState<SearchFilters | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportResults, setExportResults] = useState<ExositeEntry[]>([]);

  // Bulletproof mobile detection using ONLY CSS media queries
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [isMobile, setIsMobile] = useState(true); // Default to mobile-first
  const [isSmallMobile, setIsSmallMobile] = useState(true); // Default to small mobile-first

  useEffect(() => {
    // ONLY use matchMedia for mobile detection - never window.innerWidth
    const mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    const smallMobileMediaQuery = window.matchMedia('(max-width: 479px)');
    
    const updateMobileState = () => {
      setIsMobile(mobileMediaQuery.matches);
      setIsSmallMobile(smallMobileMediaQuery.matches);
    };
    
    const updateWindowWidth = () => {
      // Only update windowWidth for layout calculations, never for mobile detection
      setWindowWidth(window.innerWidth || 1200);
    };

    // Set initial states
    updateMobileState();
    updateWindowWidth();
    
    // Listen for media query changes (this is immune to scroll-triggered viewport changes)
    const handleMobileChange = () => {
      updateMobileState();
    };
    
    // Setup listeners for both mobile and small mobile
    if (mobileMediaQuery.addEventListener) {
      mobileMediaQuery.addEventListener('change', handleMobileChange);
      smallMobileMediaQuery.addEventListener('change', handleMobileChange);
    } else {
      mobileMediaQuery.addListener(handleMobileChange);
      smallMobileMediaQuery.addListener(handleMobileChange);
    }

    // Only listen for actual resize events for windowWidth (for layout calculations)
    const handleResize = () => {
      updateWindowWidth();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (mobileMediaQuery.removeEventListener) {
        mobileMediaQuery.removeEventListener('change', handleMobileChange);
        smallMobileMediaQuery.removeEventListener('change', handleMobileChange);
      } else {
        mobileMediaQuery.removeListener(handleMobileChange);
        smallMobileMediaQuery.removeListener(handleMobileChange);
      }
    };
  }, []);

  // Record the visit independently so the counter does not wait for database pagination.
  useEffect(() => {
    const recordVisitor = async () => {
      try {
        const visitorResponse = await fetch(`${getBackendUrl()}/visitor-count`, {
          method: 'POST',
        });

        if (visitorResponse.ok) {
          const visitorData = await visitorResponse.json();
          if (typeof visitorData.total_visits === 'number') {
            setVisitorStats({
              total_visits: visitorData.total_visits,
              counted: Boolean(visitorData.counted),
              mapped_visits: typeof visitorData.mapped_visits === 'number' ? visitorData.mapped_visits : 0,
              countries: Array.isArray(visitorData.countries) ? visitorData.countries : [],
            });
          }
        } else {
          console.warn(`Visitor counter returned ${visitorResponse.status}`);
        }
      } catch (visitorError) {
        console.warn('Visitor counter is unavailable:', visitorError);
      }
    };

    recordVisitor();
  }, []);

  // Load real data from API
  useEffect(() => {
    const loadData = async () => {
      setIsInitialLoading(true);
      setApiError(null);
      
      try {
        // Load stats
        const backendUrl = getBackendUrl();
        let statsResponse = await fetch(`${backendUrl}/stats`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!statsResponse.ok) {
          throw new Error(`Stats API returned ${statsResponse.status}: ${statsResponse.statusText}`);
        }
        
        const statsData = await statsResponse.json();
        setStats(statsData);
        setIsInitialLoading(false);
        setIsLoading(true);

        // Load ALL entries using pagination for both search and browse
        let allInitialEntries: any[] = [];
        let page = 1;
        let hasMore = true;
        
        while (hasMore) {
          const entriesResponse = await fetch(`${backendUrl}/search?per_page=100&page=${page}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          if (!entriesResponse.ok) {
            throw new Error(`Search API returned ${entriesResponse.status}: ${entriesResponse.statusText}`);
          }
          
          let entriesData;
          try {
            entriesData = await entriesResponse.json();
          } catch (jsonError) {
            console.error('Failed to parse JSON response:', jsonError);
            throw new Error('Invalid JSON response from API');
          }
          
          // Add null check and validation
          if (!entriesData || typeof entriesData !== 'object') {
            console.error('Invalid response data:', entriesData);
            throw new Error('Invalid API response format');
          }
          
          const pageEntries = entriesData.entries || [];
          
          allInitialEntries = [...allInitialEntries, ...pageEntries];
          
          // Check if there are more pages
          hasMore = pageEntries.length === 100;
          page++;
          
          // Safety check
          if (page > 10) {
            console.warn('Stopped loading after 10 pages as safety measure');
            break;
          }
        }
        
        setAllEntries(allInitialEntries);
        setSearchResults(allInitialEntries.slice(0, 10)); // Show first 10 for initial search display
        
      } catch (error) {
        console.error('Failed to load data:', error);
        // Set user-friendly error message instead of technical details
        setApiError('STINGExoDB is temporarily unavailable. Please try again later.');
        
        // Show empty state when API fails
        setStats({
          total_entries: 0,
          validated_entries: 0,
          high_confidence_entries: 0,
          pending_verification: 0,
          unique_proteins: 0,
          unique_pdb_ids: 0,
          unique_binding_partners: 0,
          unique_journals: 0,
          coverage_years: { earliest: 0, latest: 0 }
        });
        setSearchResults([]);
      } finally {
        setIsInitialLoading(false);
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSearch = async (filters: SearchFilters) => {
    setIsLoading(true);
    setCurrentSearch(filters); // Store current search for NotFound component
    setHasSearched(true); // Mark that a search was performed
    
    try {
      const params = new URLSearchParams();
      
      if (filters.protein_name) params.append('protein_name', filters.protein_name);
      if (filters.pdb_id) params.append('pdb_id', filters.pdb_id);
      if (filters.uniprot_id) params.append('uniprot_id', filters.uniprot_id);
      if (filters.organism) params.append('organism', filters.organism);
      if (filters.binding_partner) params.append('binding_partner', filters.binding_partner);
      
      const backendUrl = getBackendUrl();
      const searchUrl = `${backendUrl}/search?${params}`;
      
      const response = await fetch(searchUrl);
      
      if (!response.ok) {
        throw new Error(`Search API returned ${response.status}`);
      }
      
      const data = await response.json();
      
      
      setSearchResults(data.entries || []);
    } catch (error) {
      console.error('Search failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown search error';
      console.error('Search error details:', errorMessage);
      setSearchResults([]); // Ensure empty results on error
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to handle PDB IDs (array or comma-separated string)
  const getPdbIdArray = (pdbIds: any): string[] => {
    if (!pdbIds) return [];
    if (Array.isArray(pdbIds)) return pdbIds;
    if (typeof pdbIds === 'string') return pdbIds.split(',').map(id => id.trim());
    return [];
  };

  const handleViewStructure = (pdbId: string) => {
    
    // Find entry that contains this PDB ID
    const entry = searchResults.find(e => {
      if (e.entry_type === 'grouped') {
        const pdbIdArray = getPdbIdArray(e.grouped_entry?.representative_pdb_ids);
        return pdbIdArray.includes(pdbId);
      } else if (e.entry_type === 'individual') {
        const pdbIdArray = getPdbIdArray(e.individual_entry?.pdb_id);
        return pdbIdArray.includes(pdbId);
      }
      return false;
    });
    
    if (entry) {
      setSelectedEntry(entry);
      setShowViewer(true);
    } else {
      console.error(`❌ No entry found for PDB ID: ${pdbId}`);
    }
  };

  // ML-optimized CSV export function for search results
  const convertSearchResultsToCSV = (results: ExositeEntry[]): string => {
    if (!results || results.length === 0) return '';

    // ML-optimized headers - essential fields for machine learning
    const headers = ['protein_name', 'pdb_id', 'organism', 'exosite_residues_standard', 'experimental_method', 'binding_partner', 'confidence_level'];
    
    // Create CSV content
    let csvContent = headers.join(',') + '\n';

    results.forEach(entry => {
      const row = headers.map(header => {
        let value = '';
        
        if (entry.entry_type === 'individual' && entry.individual_entry) {
          const individualEntry = entry.individual_entry;
          
          switch (header) {
            case 'protein_name':
              value = individualEntry.protein_name || '';
              break;
            case 'pdb_id':
              value = individualEntry.pdb_id || '';
              break;
            case 'organism':
              value = individualEntry.organism || '';
              break;
            case 'exosite_residues_standard':
              value = individualEntry.exosite_residues_standard || '';
              break;
            case 'experimental_method':
              value = individualEntry.experimental_method || '';
              break;
            case 'binding_partner':
              value = individualEntry.binding_partner || individualEntry.binding_partners || '';
              break;
            case 'confidence_level':
              value = individualEntry.quality_score?.toString() || individualEntry.confidence_level || '';
              break;
            default:
              value = '';
          }
        } else if (entry.entry_type === 'grouped' && entry.grouped_entry) {
          const groupedEntry = entry.grouped_entry;
          
          switch (header) {
            case 'protein_name':
              value = groupedEntry.protein_name || '';
              break;
            case 'pdb_id':
              // For grouped entries, use first representative PDB ID
              const pdbIds = getPdbIdArray(groupedEntry.representative_pdb_ids);
              value = pdbIds.length > 0 ? pdbIds[0] : '';
              break;
            case 'organism':
              value = groupedEntry.best_entry?.organism || '';
              break;
            case 'exosite_residues_standard':
              value = groupedEntry.consensus_residues || groupedEntry.best_entry?.exosite_residues_standard || '';
              break;
            case 'experimental_method':
              value = groupedEntry.best_entry?.experimental_method || '';
              break;
            case 'binding_partner':
              value = groupedEntry.best_entry?.binding_partner || groupedEntry.best_entry?.binding_partners || '';
              break;
            case 'confidence_level':
              value = groupedEntry.evidence_quality || '';
              break;
            default:
              value = '';
          }
        }
        
        // Escape commas and quotes in CSV values
        if (typeof value === 'string') {
          value = value.replace(/"/g, '""');
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            value = `"${value}"`;
          }
        }
        return value;
      }).join(',');

      csvContent += row + '\n';
    });

    return csvContent;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50" style={{
        fontFamily: 'Univers, Arial, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        {/* Header */}
        <header style={{
          backgroundColor: '#f8f8f8',
          borderBottom: '1px solid #e7e7e7',
          color: '#2d3748',
          padding: isMobile ? '16px' : '24px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(0,0,0,0.02) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(0,0,0,0.02) 0%, transparent 50%)',
            pointerEvents: 'none'
          }} />
          
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 1,
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: '16px'
          }}>
            {/* Left: Embrapa Logo */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flex: '0 0 auto',
              minWidth: 0
            }}>
              {/* Embrapa Logo */}
              <img 
                src="/Embrapa_cnptia_transparent.png"
                alt="Embrapa"
                style={{
                  height: isMobile ? '46px' : '68px',
                  width: 'auto',
                  display: 'block',
                  objectFit: 'contain',
                  flexShrink: 0
                }}
              />
            </div>

            {/* Center: Computational Biology Research Group Logo */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1,
              width: isMobile ? '90%' : 'auto',
              maxWidth: isMobile ? '300px' : 'none'
            }}>
              <div style={{
                textAlign: 'center',
                opacity: 0.9,
                transition: 'all 0.3s ease',
                padding: isMobile ? '8px' : '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(-1px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}>
                <img 
                  src="/logo_topo_centro.gif"
                  alt="Computational Biology Research Group"
                  style={{
                    height: isSmallMobile ? '40px' : isMobile ? '50px' : windowWidth < 1024 ? '60px' : '70px',
                    width: 'auto',
                    display: 'block',
                    objectFit: 'contain'
                  }}
                />
              </div>
            </div>

            {/* Right: STING Logo */}
            {!isMobile && (
              <div style={{
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                paddingRight: '8px'
              }}>
                <img 
                  src="/logo_topo_centro_dir.gif"
                  alt="STING"
                  style={{
                    height: isMobile ? '46px' : '68px',
                    width: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                    flexShrink: 0,
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                />
              </div>
            )}
          </div>
          
          {/* Primary Navigation */}
          <div style={{
            maxWidth: '1200px',
            margin: '16px auto 0',
            position: 'relative',
            zIndex: 2,
            padding: '0 20px'
          }}>
            <nav style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '8px' : '12px',
              padding: isMobile ? '8px' : '10px',
              background: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              flexWrap: isSmallMobile ? 'wrap' : 'nowrap',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            }}>
              <button 
                onClick={() => setActiveTab('stingexodb')}
                style={{ 
                  color: activeTab === 'stingexodb' ? '#00773D' : '#4a5568', 
                  background: activeTab === 'stingexodb' ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
                  border: 'none',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '6px 12px' : '8px 16px',
                  borderRadius: '8px',
                  boxShadow: activeTab === 'stingexodb' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'stingexodb') {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'stingexodb') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                STINGExoDB
              </button>
              {!isMobile && (
                <button 
                  onClick={() => setActiveTab('stingexofind')}
                  style={{ 
                    color: activeTab === 'stingexofind' ? '#004994' : '#4a5568', 
                    background: activeTab === 'stingexofind' ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontSize: '14px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    boxShadow: activeTab === 'stingexofind' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== 'stingexofind') {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== 'stingexofind') {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  STINGExoFind
                </button>
              )}
              {!isMobile && (
                <a 
                  href="https://www.cbi.cnptia.embrapa.br/SMS/index_s.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#4a5568', 
                    background: 'transparent',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontSize: '14px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  STING Suite
                </a>
              )}
              <a
                href="#visitor-map-heading"
                style={{
                  color: '#4a5568',
                  background: 'transparent',
                  border: 'none',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '6px 12px' : '8px 16px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Access Statistics
              </a>
              <button 
                onClick={() => setActiveTab('about')}
                style={{ 
                  color: activeTab === 'about' ? '#00773D' : '#4a5568', 
                  background: activeTab === 'about' ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
                  border: 'none',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '6px 12px' : '8px 16px',
                  borderRadius: '8px',
                  boxShadow: activeTab === 'about' ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'about') {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'about') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                About
              </button>
            </nav>
            
            {/* STINGExoDB Sub-Navigation - Compact & Centered */}
            {activeTab === 'stingexodb' && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginTop: '16px',
                position: 'relative'
              }}>
                {/* Connection line */}
                <div style={{
                  position: 'absolute',
                  top: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '1px',
                  height: '8px',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), transparent)'
                }} />
                
                {/* Compact sub-navigation */}
                <nav style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px',
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '10px',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  position: 'relative',
                  maxWidth: 'fit-content'
                }}>
                  {/* Top notch for visual connection */}
                  <div style={{
                    position: 'absolute',
                    top: '-4px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '8px',
                    height: '4px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '0 0 4px 4px',
                    backdropFilter: 'blur(12px)'
                  }} />
                  
                  <button 
                    onClick={() => setActiveSubTab('search')}
                    style={{ 
                      color: activeSubTab === 'search' ? '#00773D' : '#4a5568', 
                      background: activeSubTab === 'search' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.05)',
                      border: 'none',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: '12px',
                      padding: '8px 12px',
                      borderRadius: '7px',
                      boxShadow: activeSubTab === 'search' ? '0 2px 6px rgba(0, 0, 0, 0.1)' : 'none',
                      backdropFilter: 'blur(8px)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSubTab !== 'search') {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSubTab !== 'search') {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    Search
                  </button>
                  
                  <div style={{
                    width: '1px',
                    height: '20px',
                    background: 'rgba(0, 0, 0, 0.15)'
                  }} />
                  
                  <button 
                    onClick={() => setActiveSubTab('browse')}
                    style={{ 
                      color: activeSubTab === 'browse' ? '#00773D' : '#4a5568', 
                      background: activeSubTab === 'browse' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.05)',
                      border: 'none',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: '12px',
                      padding: '8px 12px',
                      borderRadius: '7px',
                      boxShadow: activeSubTab === 'browse' ? '0 2px 6px rgba(0, 0, 0, 0.1)' : 'none',
                      backdropFilter: 'blur(8px)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSubTab !== 'browse') {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSubTab !== 'browse') {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    Browse
                  </button>
                  
                  <div style={{
                    width: '1px',
                    height: '20px',
                    background: 'rgba(0, 0, 0, 0.15)'
                  }} />
                  
                  <button 
                    onClick={() => setActiveSubTab('api')}
                    style={{ 
                      color: activeSubTab === 'api' ? '#00773D' : '#4a5568', 
                      background: activeSubTab === 'api' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.05)',
                      border: 'none',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: '12px',
                      padding: '8px 12px',
                      borderRadius: '7px',
                      boxShadow: activeSubTab === 'api' ? '0 2px 6px rgba(0, 0, 0, 0.1)' : 'none',
                      backdropFilter: 'blur(8px)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSubTab !== 'api') {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSubTab !== 'api') {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    API
                  </button>
                </nav>
              </div>
            )}
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8">
          {/* Loading State */}
          {isInitialLoading && (
            <div className="text-center" style={{ padding: '64px 0' }}>
              <div style={{
                width: '64px',
                height: '64px',
                border: '4px solid #bfdbfe',
                borderTop: '4px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 24px'
              }}></div>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '8px' }}>
                Loading STINGExoDB
              </h2>
              <p style={{ color: '#64748b' }}>Loading database...</p>
            </div>
          )}

          {/* Error State */}
          {apiError && !isInitialLoading && (
            <div className="text-center" style={{ padding: '64px 0' }}>
              <div style={{ 
                backgroundColor: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '8px', 
                padding: '24px',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626', marginBottom: '8px' }}>
                  Service Unavailable
                </h2>
                <p style={{ color: '#7f1d1d', marginBottom: '16px' }}>
                  Unable to connect to the STINGExoDB service
                </p>
                <p style={{ color: '#991b1b', fontSize: '14px', fontFamily: 'monospace' }}>
                  Error: {apiError}
                </p>
              </div>
            </div>
          )}

          {/* Main Content - only show when not loading and no error */}
          {!isInitialLoading && !apiError && (
            <>
              {/* Hero Section for Search - Only show on STINGExoDB search tab */}
              {activeTab === 'stingexodb' && activeSubTab === 'search' && (
                <div className="text-center mb-12">
                  <h2 style={{ 
                    fontSize: '36px', 
                    fontWeight: 'bold', 
                    color: '#1e293b', 
                    marginBottom: '16px' 
                  }}>
                    <span className="text-gradient">STINGExoDB</span>
                  </h2>
                  <p style={{ 
                    fontSize: '20px', 
                    color: '#64748b', 
                    maxWidth: '768px', 
                    margin: '0 auto 32px' 
                  }}>
                    A comprehensive database of validated protein exosites with 
                    evidence-based curation and 3D visualization.
                  </p>
              
                  <SearchBar 
                    onSearch={handleSearch}
                    loading={isLoading}
                  />
                </div>
              )}

              {/* Stats Dashboard - Only show on STINGExoDB sub-tabs and not on mobile */}
              {stats && activeTab === 'stingexodb' && (activeSubTab === 'search' || activeSubTab === 'browse' || activeSubTab === 'api') && !isMobile && (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  gap: '24px', 
                  marginBottom: '32px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <div className="card text-center" style={{ flex: '1', minWidth: '200px', maxWidth: '300px', position: 'relative' }}>
                    <div style={{ 
                      fontSize: '36px', 
                      fontWeight: 'bold', 
                      color: '#22c55e', 
                      marginBottom: '8px' 
                    }}>
                      {(stats.total_entries || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: '16px', color: '#64748b', marginBottom: '8px' }}>Total Entries</div>
                  </div>
                  
                  <div className="card text-center" style={{ flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                    <div style={{ 
                      fontSize: '36px', 
                      fontWeight: 'bold', 
                      color: '#3b82f6', 
                      marginBottom: '8px' 
                    }}>
                      {(stats.unique_proteins || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: '16px', color: '#64748b' }}>Unique Proteins</div>
                  </div>
                  
                  <div className="card text-center" style={{ flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                    <div style={{ 
                      fontSize: '36px', 
                      fontWeight: 'bold', 
                      color: '#8b5cf6', 
                      marginBottom: '8px' 
                    }}>
                      {(stats.unique_pdb_ids || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: '16px', color: '#64748b' }}>Unique PDB Structures</div>
                  </div>
                  
                  <div className="card text-center" style={{ flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                    <div style={{ 
                      fontSize: '36px', 
                      fontWeight: 'bold', 
                      color: '#f59e0b', 
                      marginBottom: '8px' 
                    }}>
                      {(stats.unique_binding_partners || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: '16px', color: '#64748b' }}>Unique Binding Partners</div>
                  </div>

                </div>
              )}

              {/* Conditional Content Based on Active Tab and SubTab */}
              {activeTab === 'stingexodb' && activeSubTab === 'search' ? (
                <>
                  {/* Search Results Section */}
                  <div style={{ width: '100%' }}>
                    {isLoading ? (
                      <div className="text-center" style={{ padding: '48px 0' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          border: '4px solid #bfdbfe',
                          borderTop: '4px solid #3b82f6',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          margin: '0 auto 16px'
                        }}></div>
                        <p style={{ color: '#64748b' }}>Searching database...</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {searchResults.length > 0 ? (
                          <>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              marginBottom: '16px'
                            }}>
                              <h3 style={{ 
                                fontSize: '18px', 
                                fontWeight: '600', 
                                color: '#1e293b',
                                margin: 0
                              }}>
                                Search Results ({searchResults.length})
                              </h3>
                              
                              {searchResults.length > 0 && (
                                <button
                                  onClick={() => {
                                    setShowExportModal(true);
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '12px 20px',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                                    position: 'relative',
                                    letterSpacing: '-0.005em'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                    e.currentTarget.style.borderColor = '#d1d5db';
                                    e.currentTarget.style.color = '#1f2937';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                    e.currentTarget.style.color = '#374151';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
                                  }}
                                  title="Export search results to CSV"
                                >
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '18px',
                                    height: '18px',
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: '5px',
                                    flexShrink: 0
                                  }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                      <polyline points="7,10 12,15 17,10"/>
                                      <line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                  </div>
                                  <span style={{ fontSize: '14px', fontWeight: '600' }}>
                                    Export Search Results
                                  </span>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '20px',
                                    height: '18px',
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: '9px',
                                    fontSize: '10px',
                                    fontWeight: '700',
                                    paddingLeft: '6px',
                                    paddingRight: '6px',
                                    color: '#6b7280'
                                  }}>
                                    {searchResults.length}
                                  </div>
                                </button>
                              )}
                            </div>
                            
                            {searchResults.map((entry, index) => {
                              const entryId = entry.entry_type === 'individual' 
                                ? `search-individual-${entry.individual_entry?.id || index}`
                                : `search-grouped-${entry.grouped_entry?.protein_name || index}`.replace(/\s+/g, '-');
                              const hasViewer = showViewer && selectedEntry && 
                                ((selectedEntry.entry_type === 'individual' && selectedEntry.individual_entry?.id === entry.individual_entry?.id) ||
                                 (selectedEntry.entry_type === 'grouped' && selectedEntry.grouped_entry?.protein_name === entry.grouped_entry?.protein_name));
                              
                              return (
                                <div key={entryId} style={{ width: '100%' }}>
                                  <div style={{ 
                                    display: 'flex', 
                                    gap: hasViewer ? '24px' : '0',
                                    alignItems: hasViewer ? 'flex-start' : 'stretch'
                                  }}>
                                    <div style={{ 
                                      width: hasViewer ? 'calc(100% - 504px)' : '100%',  // 504px = 480px viewer + 24px gap
                                      transition: 'width 0.3s ease'
                                    }}>
                                      <ExositeCard
                                        entry={entry}
                                        onViewStructure={(pdbId) => {
                                          if (hasViewer) {
                                            // Close if already open
                                            setShowViewer(false);
                                            setSelectedEntry(null);
                                          } else {
                                            // Open for this entry
                                            setSelectedEntry(entry);
                                            setShowViewer(true);
                                          }
                                        }}
                                      />
                                    </div>
                                    
                                    {/* 3D Viewer */}
                                    {hasViewer && (() => {
                                      const shouldShow = selectedEntry.entry_type === 'grouped' ? 
                                        getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids).length > 0 :
                                        selectedEntry.individual_entry?.pdb_id;
                                      
                                      if (shouldShow) {
                                        const pdbId = selectedEntry.entry_type === 'grouped' 
                                          ? getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids)[0] || ''
                                          : getPdbIdArray(selectedEntry.individual_entry?.pdb_id)[0] || selectedEntry.individual_entry?.pdb_id || '';
                                        
                                        return (
                                          <div style={{ 
                                            width: '480px', 
                                            flexShrink: 0,
                                            transition: 'opacity 0.3s ease'
                                          }}>
                                            <ProteinViewer
                                              pdbId={pdbId}
                                              validatedResidues={
                                                selectedEntry.entry_type === 'grouped'
                                                  ? selectedEntry.grouped_entry?.best_entry?.validated_pdb_residues
                                                  : selectedEntry.individual_entry?.validated_pdb_residues
                                              }
                                              mappingStatus={
                                                selectedEntry.entry_type === 'grouped'
                                                  ? selectedEntry.grouped_entry?.best_entry?.pdb_mapping_status
                                                  : selectedEntry.individual_entry?.pdb_mapping_status
                                              }
                                              mappingValidationReason={
                                                selectedEntry.entry_type === 'grouped'
                                                  ? selectedEntry.grouped_entry?.best_entry?.pdb_mapping_validation_reason
                                                  : selectedEntry.individual_entry?.pdb_mapping_validation_reason
                                              }
                                              availablePdbIds={
                                                selectedEntry.entry_type === 'grouped'
                                                  ? getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids)
                                                  : getPdbIdArray(selectedEntry.individual_entry?.pdb_id)
                                              }
                                              representativePdbId={pdbId}
                                              bindingPartners={
                                                selectedEntry.entry_type === 'grouped'
                                                  ? selectedEntry.grouped_entry?.best_entry?.binding_partner || 
                                                    selectedEntry.grouped_entry?.best_entry?.binding_partners
                                                  : selectedEntry.individual_entry?.binding_partner || 
                                                    selectedEntry.individual_entry?.binding_partners
                                              }
                                              onClose={() => {
                                                setShowViewer(false);
                                                setSelectedEntry(null);
                                              }}
                                              onExpand={() => {
                                                setShowFullscreenViewer(true);
                                              }}
                                              onPdbChange={(newPdbId) => {
                                                // Handle PDB change
                                              }}
                                            />
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          hasSearched && currentSearch && (
                            <NotFoundSuggestions
                              query={currentSearch!._originalQuery || ''}
                              searchType={currentSearch!._searchType || 'protein_name'}
                              onRetry={() => {
                                setCurrentSearch(null);
                                setHasSearched(false);
                                setSearchResults([]);
                              }}
                              onBrowse={() => {
                                setActiveSubTab('browse');
                                setCurrentSearch(null);
                                setHasSearched(false);
                              }}
                            />
                          )
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : activeTab === 'stingexodb' && activeSubTab === 'browse' ? (
                /* Browse Tab Content */
                <Browse 
                  entries={allEntries} 
                />
              ) : activeTab === 'stingexodb' && activeSubTab === 'api' ? (
                /* API Tab Content */
                <APIComponent />
              ) : activeTab === 'stingexofind' ? (
                /* STINGExoFind Prediction Tab Content */
                <ExositePrediction isMobile={isMobile} isSmallMobile={isSmallMobile} />
              ) : activeTab === 'suite' ? (
                /* STING Suite Tab Content */
                <div className="max-w-7xl mx-auto px-4 py-8">
                  <div className="text-center mb-12">
                    <h1 style={{ fontSize: '48px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', letterSpacing: '2px' }}>
                      STING Suite
                    </h1>
                    <p style={{ fontSize: '20px', color: '#64748b', marginBottom: '8px' }}>
                      Comprehensive Computational Biology Tools
                    </p>
                    <p style={{ fontSize: '16px', color: '#94a3b8' }}>
                      Embrapa Digital Agriculture Research Group
                    </p>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '32px' }}>
                    
                    {/* STINGExoDB Card */}
                    <div className="card" style={{ padding: '32px', background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: 'white', borderRadius: '20px', boxShadow: '0 12px 32px rgba(34, 197, 94, 0.3)' }}>
                      <h3 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                        STINGExoDB
                      </h3>
                      <p style={{ fontSize: '16px', marginBottom: '24px', opacity: 0.9, lineHeight: '1.6' }}>
                        Comprehensive database of validated protein exosites with evidence-based curation, 3D visualization, and API access.
                      </p>
                      <button 
                        onClick={() => setActiveTab('stingexodb')}
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          border: 'none',
                          color: 'white',
                          padding: '12px 24px',
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          backdropFilter: 'blur(10px)',
                          textTransform: 'uppercase',
                          letterSpacing: '1px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                        }}
                      >
                        Access Database
                      </button>
                    </div>
                    
                    {/* STINGAllo Card */}
                    <div className="card" style={{ padding: '32px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white', borderRadius: '20px', boxShadow: '0 12px 32px rgba(59, 130, 246, 0.3)' }}>
                      <h3 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                        STINGAllo
                      </h3>
                      <p style={{ fontSize: '16px', marginBottom: '24px', opacity: 0.9, lineHeight: '1.6' }}>
                        Advanced allosteric site prediction using per-residue nanoenvironment analysis. Identifies potential allosteric forming residues (AFRs).
                      </p>
                      <a 
                        href="https://www.stingallo.cbi.cnptia.embrapa.br/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          border: 'none',
                          color: 'white',
                          padding: '12px 24px',
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '600',
                          textDecoration: 'none',
                          display: 'inline-block',
                          transition: 'all 0.3s ease',
                          backdropFilter: 'blur(10px)',
                          textTransform: 'uppercase',
                          letterSpacing: '1px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                        }}
                      >
                        Launch STINGAllo →
                      </a>
                    </div>
                    
                    {/* CBI Portal Card */}
                    <div className="card" style={{ padding: '32px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', borderRadius: '20px', boxShadow: '0 12px 32px rgba(245, 158, 11, 0.3)' }}>
                      <h3 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                        CBI Portal
                      </h3>
                      <p style={{ fontSize: '16px', marginBottom: '24px', opacity: 0.9, lineHeight: '1.6' }}>
                        Computational Biology Research Group main portal. Access to all tools, publications, and research resources.
                      </p>
                      <a 
                        href="https://www.cbi.cnptia.embrapa.br/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          border: 'none',
                          color: 'white',
                          padding: '12px 24px',
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '600',
                          textDecoration: 'none',
                          display: 'inline-block',
                          transition: 'all 0.3s ease',
                          backdropFilter: 'blur(10px)',
                          textTransform: 'uppercase',
                          letterSpacing: '1px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                        }}
                      >
                        Visit CBI Portal →
                      </a>
                    </div>
                    
                    {/* STINGExoFind Placeholder Card */}
                    <div className="card" style={{ padding: '32px', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', borderRadius: '20px', boxShadow: '0 12px 32px rgba(99, 102, 241, 0.3)', position: 'relative' }}>
                      <div style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: 'rgba(245, 158, 11, 0.9)',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        Coming Soon
                      </div>
                      <h3 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                        STINGExoFind
                      </h3>
                      <p style={{ fontSize: '16px', marginBottom: '24px', opacity: 0.9, lineHeight: '1.6' }}>
                        Machine learning-powered exosite prediction engine. Upload protein structures and discover allosteric binding sites.
                      </p>
                      <button 
                        onClick={() => setActiveTab('stingexofind')}
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          border: 'none',
                          color: 'white',
                          padding: '12px 24px',
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          backdropFilter: 'blur(10px)',
                          textTransform: 'uppercase',
                          letterSpacing: '1px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                        }}
                      >
                        View Preview
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* About Tab Content */
                <div className="card text-center" style={{ maxWidth: '800px', margin: '0 auto', padding: '64px 32px' }}>
                  <img 
                    src="/embrapa-cnptia.png" 
                    alt="Embrapa CNPTIA Logo" 
                    style={{
                      height: '80px',
                      width: 'auto',
                      maxWidth: '400px',
                      margin: '0 auto 24px',
                      display: 'block'
                    }}
                  />
                  <h2 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1e293b', marginBottom: '16px' }}>
                    Protein Exosites Research Platform
                  </h2>
                  <p style={{ fontSize: '18px', color: '#64748b', lineHeight: '1.6', marginBottom: '12px' }}>
                    Platform for protein exosites research, combining validated database curation (STINGExoDB) 
                    with advanced machine learning-powered prediction capabilities (STINGExoFind). 
                    Features evidence-based curation, 3D visualization, and programmatic access through a modern REST API.
                  </p>
                  <p style={{ fontSize: '16px', color: '#22c55e', fontWeight: '600', marginBottom: '24px' }}>
                    Developed by Embrapa Digital Computational Biology Research Group
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                        Validated Database
                      </h4>
                      <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.5' }}>
                        STINGExoDB provides comprehensive, experimentally validated protein exosite data with complete evidence tracking.
                      </p>
                    </div>
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                        Prediction Engine
                      </h4>
                      <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.5' }}>
                        STINGExoFind offers advanced machine learning algorithms for discovering potential exosites in protein structures.
                      </p>
                    </div>
                    <div>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                        3D Visualization
                      </h4>
                      <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.5' }}>
                        Interactive molecular viewers with exosite highlighting and comprehensive structural analysis capabilities.
                      </p>
                    </div>
                  </div>
                  <div style={{ 
                    backgroundColor: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '24px',
                    textAlign: 'left'
                  }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
                      Research Institution
                    </h4>
                    <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.5', margin: 0 }}>
                      <strong>Embrapa Digital Agriculture</strong><br/>
                      Computational Biology Research Group<br/>
                      Av. André Tosello, nº 209, Campus da Unicamp<br/>
                      Barão Geraldo, Campinas - SP - Brazil
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </main>

        {/* Footer */}
        <footer style={{
          backgroundColor: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
          marginTop: '64px',
          padding: '48px 0 32px'
        }}>
          <div className="max-w-7xl mx-auto px-4">
            <VisitorMapFooter stats={visitorStats} />

            {/* Citation, License, Version Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: isMobile ? '32px' : '48px',
              marginBottom: '48px',
              textAlign: 'left'
            }}>
              {/* How to Cite */}
              <div>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  How to Cite
                </h3>
                <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6' }}>
                  <p style={{ marginBottom: '8px' }}>
                    <strong>Published Article</strong>
                  </p>
                  <p style={{ marginBottom: '8px' }}>
                    Please cite:
                  </p>
                  <div style={{
                    backgroundColor: '#ffffff',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    color: '#475569'
                  }}>
                    Omage, F.B., Mazoni, I., Yano, I.H. and Neshich, G. (2026). STING-ExositeDB: An AI-assisted curated database of protein exosites for drug discovery. <em>Database</em>, 2026, baag031.{' '}
                    <a
                      href="https://doi.org/10.1093/database/baag031"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#3b82f6', textDecoration: 'none' }}
                    >
                      https://doi.org/10.1093/database/baag031
                    </a>
                  </div>
                </div>
              </div>

              {/* License & Terms */}
              <div>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  License & Terms
                </h3>
                <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6' }}>
                  <p style={{ marginBottom: '8px' }}>
                    This database is made available under the{' '}
                    <a
                      href="https://creativecommons.org/licenses/by/4.0/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}
                    >
                      Creative Commons Attribution 4.0 International License (CC BY 4.0)
                    </a>.
                  </p>
                  <p>
                    You are free to share and adapt the data with appropriate attribution.
                  </p>
                </div>
              </div>

              {/* Version & Updates */}
              <div>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Database Info
                </h3>
                <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6' }}>
                  <p style={{ marginBottom: '6px' }}>
                    <strong>Version:</strong> 2.0.0
                  </p>
                  <p style={{ marginBottom: '6px' }}>
                    <strong>Last Updated:</strong> August 2026
                  </p>
                  <p style={{ marginBottom: '6px' }}>
                    <strong>Total Entries:</strong> {stats?.total_entries || 0}
                  </p>
                  <p>
                    <strong>Unique Proteins:</strong> {stats?.unique_proteins || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact & Copyright */}
            <div className="text-center" style={{
              color: '#64748b',
              paddingTop: '32px',
              borderTop: '1px solid #e2e8f0'
            }}>
              <p style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600', color: '#475569' }}>
                Protein Exosites Research Platform
              </p>
              <p style={{ marginBottom: '8px', fontSize: '14px' }}>
                Computational Biology Research Group, Embrapa Digital Agriculture
              </p>
              <p style={{ marginBottom: '8px', fontSize: '14px' }}>
                © 2023 - 2026. All Rights Reserved.
              </p>
              <p style={{ fontSize: '13px', lineHeight: '1.5' }}>
                Av. André Tosello, nº 209, Campus da Unicamp, Barão Geraldo<br/>
                Caixa Postal: 6041 CEP: 13083-886 - Campinas - SP - Brazil<br/>
                Phone: 3211-5774 | Fax: 3211-5754
              </p>
            </div>
          </div>
        </footer>

        {/* Export Modal */}
        <ExportModal 
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          currentFilters={currentSearch}
          specificResults={searchResults}
        />

        {/* Fullscreen 3D Viewer Modal */}
        {showFullscreenViewer && selectedEntry && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              maxWidth: '1400px',
              maxHeight: '900px',
              backgroundColor: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}>
              <ProteinViewer
                pdbId={
                  selectedEntry.entry_type === 'grouped' 
                    ? getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids)[0] || ''
                    : getPdbIdArray(selectedEntry.individual_entry?.pdb_id)[0] || selectedEntry.individual_entry?.pdb_id || ''
                }
                validatedResidues={
                  selectedEntry.entry_type === 'grouped'
                    ? selectedEntry.grouped_entry?.best_entry?.validated_pdb_residues
                    : selectedEntry.individual_entry?.validated_pdb_residues
                }
                mappingStatus={
                  selectedEntry.entry_type === 'grouped'
                    ? selectedEntry.grouped_entry?.best_entry?.pdb_mapping_status
                    : selectedEntry.individual_entry?.pdb_mapping_status
                }
                mappingValidationReason={
                  selectedEntry.entry_type === 'grouped'
                    ? selectedEntry.grouped_entry?.best_entry?.pdb_mapping_validation_reason
                    : selectedEntry.individual_entry?.pdb_mapping_validation_reason
                }
                availablePdbIds={
                  selectedEntry.entry_type === 'grouped'
                    ? getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids)
                    : getPdbIdArray(selectedEntry.individual_entry?.pdb_id)
                }
                representativePdbId={
                  selectedEntry.entry_type === 'grouped' 
                    ? getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids)[0] || ''
                    : getPdbIdArray(selectedEntry.individual_entry?.pdb_id)[0] || selectedEntry.individual_entry?.pdb_id || ''
                }
                bindingPartners={
                  selectedEntry.entry_type === 'grouped'
                    ? selectedEntry.grouped_entry?.best_entry?.binding_partner || 
                      selectedEntry.grouped_entry?.best_entry?.binding_partners
                    : selectedEntry.individual_entry?.binding_partner || 
                      selectedEntry.individual_entry?.binding_partners
                }
                isFullscreen={true}
                onClose={() => {
                  setShowFullscreenViewer(false);
                }}
                onPdbChange={(newPdbId) => {
                  // Handle PDB change in fullscreen
                }}
              />
            </div>
          </div>
        )}

        {/* Add spinning animation */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </QueryClientProvider>
  );
}

export default App;
